import React, { useEffect, useState } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { X, Gift } from 'lucide-react';
interface SpinWheelProps {
  onClose: () => void;
  onWin: (prize: string) => void;
}
export function SpinWheel({
  onClose,
  onWin
}: SpinWheelProps) {
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const controls = useAnimation();
  const prizes = ['10% OFF', '€5 OFF', 'Free Shipping', '15% OFF', 'Buy 1 Get 1', 'Better Luck Next Time', '20% OFF', '€10 OFF'];
  const colors = ['#EF4444', '#F59E0B', '#10B981', '#3B82F6', '#8B5CF6', '#EC4899', '#06B6D4', '#F97316'];
  const spinWheel = () => {
    if (spinning) return;
    setSpinning(true);
    setResult(null);
    // Random number of full rotations (3-5) plus a random prize segment
    const randomPrizeIndex = Math.floor(Math.random() * prizes.length);
    const randomRotations = 3 + Math.floor(Math.random() * 3);
    const degreesToPrize = 360 - randomPrizeIndex * (360 / prizes.length);
    const totalDegrees = randomRotations * 360 + degreesToPrize;
    controls.start({
      rotate: totalDegrees,
      transition: {
        duration: 5,
        ease: [0.2, 0.0, 0.0, 1.0] // Custom easing for realistic spin effect
      }
    }).then(() => {
      setSpinning(false);
      setResult(prizes[randomPrizeIndex]);
      onWin(prizes[randomPrizeIndex]);
    });
  };
  return <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-id="element-363">
      <div className="bg-white rounded-xl max-w-md w-full animate-fade-in p-6" data-id="element-364">
        <div className="flex justify-between items-center mb-4" data-id="element-365">
          <h2 className="text-xl font-bold" data-id="element-366">Spin & Win</h2>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-100" data-id="element-367">
            <X size={20} data-id="element-368" />
          </button>
        </div>
        {result ? <div className="text-center py-8" data-id="element-369">
            <div className="inline-block p-5 rounded-full bg-yellow-100 mb-4" data-id="element-370">
              <Gift size={40} className="text-yellow-500" data-id="element-371" />
            </div>
            <h3 className="text-2xl font-bold mb-2" data-id="element-372">
              {result === 'Better Luck Next Time' ? 'Better Luck Next Time!' : 'Congratulations!'}
            </h3>
            <p className="text-gray-600 mb-4" data-id="element-373">
              {result === 'Better Luck Next Time' ? "Don't worry, you can try again tomorrow." : `You've won ${result}!`}
            </p>
            {result !== 'Better Luck Next Time' && <div className="bg-gray-100 p-3 rounded-lg mb-4" data-id="element-374">
                <p className="font-mono font-bold tracking-wider" data-id="element-375">
                  {result.replace(/\s/g, '').toUpperCase()}
                </p>
              </div>}
            <div className="flex space-x-3" data-id="element-376">
              <button onClick={onClose} className="flex-1 py-2 border border-gray-300 rounded-lg" data-id="element-377">
                Close
              </button>
              <button onClick={() => {
            setResult(null);
            spinWheel();
          }} className="flex-1 py-2 bg-green-500 text-white rounded-lg" disabled={spinning} data-id="element-378">
                Spin Again
              </button>
            </div>
          </div> : <div className="relative" data-id="element-379">
            <div className="relative w-64 h-64 mx-auto" data-id="element-380">
              {/* Wheel segments */}
              <svg viewBox="0 0 100 100" className="w-full h-full" data-id="element-381">
                <motion.g animate={controls} style={{
              originX: '50px',
              originY: '50px'
            }} data-id="element-382">
                  {prizes.map((_, index) => {
                const angle = 360 / prizes.length;
                const startAngle = index * angle;
                const endAngle = (index + 1) * angle;
                const startRad = startAngle * Math.PI / 180;
                const endRad = endAngle * Math.PI / 180;
                const x1 = 50 + 50 * Math.cos(startRad);
                const y1 = 50 + 50 * Math.sin(startRad);
                const x2 = 50 + 50 * Math.cos(endRad);
                const y2 = 50 + 50 * Math.sin(endRad);
                const largeArcFlag = angle > 180 ? 1 : 0;
                return <path key={index} d={`M 50 50 L ${x1} ${y1} A 50 50 0 ${largeArcFlag} 1 ${x2} ${y2} Z`} fill={colors[index % colors.length]} data-id="element-383" />;
              })}
                  {/* Prize labels */}
                  {prizes.map((prize, index) => {
                const angle = 360 / prizes.length;
                const midAngle = index * angle + angle / 2;
                const midRad = midAngle * Math.PI / 180;
                // Position text at 75% of radius for better readability
                const x = 50 + 35 * Math.cos(midRad);
                const y = 50 + 35 * Math.sin(midRad);
                return <text key={index} x={x} y={y} fill="white" fontSize="4" fontWeight="bold" textAnchor="middle" alignmentBaseline="middle" transform={`rotate(${midAngle}, ${x}, ${y})`} data-id="element-384">
                        {prize}
                      </text>;
              })}
                </motion.g>
              </svg>
              {/* Center circle */}
              <div className="absolute inset-0 flex items-center justify-center" data-id="element-385">
                <div className="w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center border-4 border-gray-200" data-id="element-386">
                  <div className="w-8 h-8 rounded-full bg-yellow-500" data-id="element-387" />
                </div>
              </div>
              {/* Pointer */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1" data-id="element-388">
                <div className="w-4 h-4 bg-white transform rotate-45" data-id="element-389" />
              </div>
            </div>
            <button onClick={spinWheel} disabled={spinning} className={`mt-6 w-full py-3 rounded-lg font-medium text-white transition ${spinning ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-500 hover:bg-green-600'}`} data-id="element-390">
              {spinning ? 'Spinning...' : 'Spin Now'}
            </button>
          </div>}
      </div>
    </div>;
}